import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransferStateService } from '@scullyio/ng-lib';

@Injectable({
  providedIn: 'root'
})
export class AboutUsResolver implements Resolve<any> {
  constructor(private http: HttpClient,
    private transferStateService:TransferStateService) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const lang = route.url[0].path;
    const isTeamPage = route.url.some(segment => segment.path === 'team');

    // Define paths for main data and metadata
    const mainDataUrl = `../../../assets/${environment.projectName}/pages-components.json`;
    const metadataUrl = `../../../assets/${environment.projectName}/content/${lang}/${isTeamPage ? 'team-page' : 'aboutus-page'}/meta-data.json`;

    // Fetch the main JSON file and metadata file in parallel
    return forkJoin({
      mainData: this.transferStateService.useScullyTransferState('maindata',this.http.get<any>(mainDataUrl).pipe(catchError(() => of(null)))),
      metadata: this.transferStateService.useScullyTransferState('metadata',this.http.get<any>(metadataUrl).pipe(catchError(() => of(null))))
    }).pipe(
      switchMap(({ mainData, metadata }) => {
        if (mainData && mainData[isTeamPage ? "teamComponents" : "aboutusComponents"]?.length > 0) {
          // For each component in aboutusComponents, fetch the corresponding JSON file based on language
          const componentWithDetailsRequests = mainData[isTeamPage ? "teamComponents" : "aboutusComponents"].map((item: any) => {
            const fileName = item.fileName;
            const subJsonPath = `../../../assets/${environment.projectName}/content/${lang}/aboutus-page/${fileName}.json`;
            
            // Fetch each sub-component JSON and add it as `details` to the item
            return this.transferStateService.useScullyTransferState('subJsOn',this.http.get(subJsonPath).pipe(
              map((details) => ({ ...item, details })), // Add fetched JSON as `details`
              catchError((error) => {
                console.error(`Failed to fetch ${fileName}:`, error);
                return of({ ...item, details: null }); // Return item with `details` as null on error
              })
            ));
          });

          // Use forkJoin to wait for all component details requests to complete
          return forkJoin(componentWithDetailsRequests).pipe(
            map((componentsWithDetails) => {
              // Return main data enriched with aboutusComponents and metadata
              return { ...mainData, aboutusComponents: componentsWithDetails, metadata };
            })
          );
        } else {
          console.warn("No components found in aboutusComponents.");
          return of({ ...mainData, metadata }); // Return main data with metadata if no components found
        }
      }),
      catchError((error) => {
        console.error('Error fetching data:', error);
        return of(null); // Handle errors in fetching data
      })
    );
  }
}
